import { create } from "zustand";
import LZString from "lz-string";
import marketdata from "../data/Services/marketdata";

/**
 * Store for custom indicator defaults
 * Allows users to save their preferred settings for each indicator
 * Now synced with marketdata server with smart compression
 *
 * Structure: {
 *   [indicatorKey]: {
 *     config: { windowSize: 20, sourcePath: 'close', ... },
 *     appearanceConfig: { stroke: { color: '#FF0000', width: 2 }, ... }
 *   }
 * }
 */

// Compression threshold: only compress if we save at least 20% (0.8 ratio)
const COMPRESSION_THRESHOLD = 0.8;

/**
 * Compress data if it provides significant savings
 * @param {object} data - Data to potentially compress
 * @returns {object} - { compressed: boolean, data: string }
 */
const smartCompress = (data) => {
  const jsonString = JSON.stringify(data);
  const originalSize = new Blob([jsonString]).size;

  const compressed = LZString.compressToUTF16(jsonString);
  const compressedSize = new Blob([compressed]).size;

  const ratio = compressedSize / originalSize;
  const savedBytes = originalSize - compressedSize;

  console.log(`[Indicator Defaults] Compression analysis:`, {
    originalSize: `${originalSize} bytes`,
    compressedSize: `${compressedSize} bytes`,
    ratio: `${(ratio * 100).toFixed(1)}%`,
    savedBytes: `${savedBytes} bytes`,
    useCompression: ratio < COMPRESSION_THRESHOLD,
  });

  // Only use compression if we save at least 20%
  if (ratio < COMPRESSION_THRESHOLD) {
    return { compressed: true, data: compressed };
  }

  return { compressed: false, data: jsonString };
};

/**
 * Decompress data if it was compressed
 * @param {object} stored - { compressed: boolean, data: string }
 * @returns {object} - Decompressed data
 */
const smartDecompress = (stored) => {
  if (!stored) return {};

  // Handle old localStorage format (direct object)
  if (typeof stored === "object" && !stored.compressed && !stored.data) {
    return stored;
  }

  if (stored.compressed) {
    const decompressed = LZString.decompressFromUTF16(stored.data);
    return JSON.parse(decompressed);
  }

  return JSON.parse(stored.data);
};

export const useIndicatorDefaultsStore = create((set, get) => ({
  // Custom defaults storage: { [indicatorKey]: { config, appearanceConfig } }
  customDefaults: {},

  // Loading state
  isLoading: false,
  isInitialized: false,

  /**
   * Load defaults from marketdata server
   */
  loadFromServer: async () => {
    if (!marketdata.user) {
      console.log(
        "[Indicator Defaults] No user logged in, skipping server load",
      );
      set({ isInitialized: true });
      return;
    }

    set({ isLoading: true });
    try {
      const data = await marketdata.getDefaults();
      if (data) {
        const decompressed = smartDecompress(data);
        set({ customDefaults: decompressed || {}, isInitialized: true });
        console.log(
          "[Indicator Defaults] Loaded from server:",
          Object.keys(decompressed || {}).length,
          "indicators",
        );
      } else {
        // No data yet, start with empty defaults
        console.log(
          "[Indicator Defaults] No data on server yet, starting fresh",
        );
        set({ customDefaults: {}, isInitialized: true });
      }
    } catch (error) {
      // Server error or no data - start with empty defaults
      console.log(
        "[Indicator Defaults] No saved defaults found on server (first time), starting fresh",
      );
      set({ customDefaults: {}, isInitialized: true });
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Save defaults to marketdata server
   */
  saveToServer: async () => {
    if (!marketdata.user) {
      console.log(
        "[Indicator Defaults] No user logged in, skipping server save",
      );
      return;
    }

    const { customDefaults } = get();
    try {
      const compressed = smartCompress(customDefaults);
      // Save the wrapper object as JSON string (like saveDesktop does)
      const content = JSON.stringify(compressed);
      await marketdata.saveDefaults(content);
      console.log("[Indicator Defaults] Saved to server successfully");
    } catch (error) {
      console.warn(
        "[Indicator Defaults] Could not save to server (server may not support this feature yet)",
      );
      // Don't throw - saving locally is still valid even if server save fails
    }
  },

  /**
   * Save both config and appearance defaults for an indicator
   * @param {string} indicatorKey - Unique indicator key (e.g., 'sma', 'rsi')
   * @param {object} config - Configuration options (windowSize, sourcePath, etc.)
   * @param {object} appearanceConfig - Appearance options (stroke, fill, etc.)
   */
  saveAllDefaults: async (indicatorKey, config, appearanceConfig) => {
    set((state) => ({
      customDefaults: {
        ...state.customDefaults,
        [indicatorKey]: {
          config: config || {},
          appearanceConfig: appearanceConfig || {},
        },
      },
    }));
    await get().saveToServer();
  },

  /**
   * Save only appearance defaults for an indicator (preserve existing config)
   * @param {string} indicatorKey - Unique indicator key
   * @param {object} appearanceConfig - Appearance options
   */
  saveAppearanceDefaults: async (indicatorKey, appearanceConfig) => {
    set((state) => {
      const existing = state.customDefaults[indicatorKey] || {};
      return {
        customDefaults: {
          ...state.customDefaults,
          [indicatorKey]: {
            ...existing,
            appearanceConfig: appearanceConfig || {},
          },
        },
      };
    });
    await get().saveToServer();
  },

  /**
   * Reset both config and appearance defaults for a specific indicator
   * @param {string} indicatorKey - Unique indicator key
   */
  resetAllDefaults: async (indicatorKey) => {
    set((state) => {
      const { [indicatorKey]: _, ...rest } = state.customDefaults;
      return { customDefaults: rest };
    });
    await get().saveToServer();
  },

  /**
   * Reset only appearance defaults for a specific indicator (preserve config)
   * @param {string} indicatorKey - Unique indicator key
   */
  resetAppearanceDefaults: async (indicatorKey) => {
    set((state) => {
      const existing = state.customDefaults[indicatorKey];
      if (!existing) return state;

      const { appearanceConfig, ...rest } = existing;

      // If only appearanceConfig existed, remove the entire entry
      if (
        Object.keys(rest).length === 0 ||
        !rest.config ||
        Object.keys(rest.config).length === 0
      ) {
        const { [indicatorKey]: _, ...remainingDefaults } =
          state.customDefaults;
        return { customDefaults: remainingDefaults };
      }

      // Otherwise, just remove appearanceConfig
      return {
        customDefaults: {
          ...state.customDefaults,
          [indicatorKey]: rest,
        },
      };
    });
    await get().saveToServer();
  },

  /**
   * Get all custom defaults for an indicator
   * @param {string} indicatorKey - Unique indicator key
   * @returns {{ config: object, appearanceConfig: object } | undefined}
   */
  getDefaults: (indicatorKey) => get().customDefaults[indicatorKey],

  /**
   * Get only config defaults for an indicator
   * @param {string} indicatorKey - Unique indicator key
   * @returns {object | undefined}
   */
  getConfigDefaults: (indicatorKey) =>
    get().customDefaults[indicatorKey]?.config,

  /**
   * Get only appearance defaults for an indicator
   * @param {string} indicatorKey - Unique indicator key
   * @returns {object | undefined}
   */
  getAppearanceDefaults: (indicatorKey) =>
    get().customDefaults[indicatorKey]?.appearanceConfig,

  /**
   * Check if indicator has custom config defaults
   * @param {string} indicatorKey - Unique indicator key
   * @returns {boolean}
   */
  hasConfigDefaults: (indicatorKey) => {
    const defaults = get().customDefaults[indicatorKey];
    return !!(defaults?.config && Object.keys(defaults.config).length > 0);
  },

  /**
   * Check if indicator has custom appearance defaults
   * @param {string} indicatorKey - Unique indicator key
   * @returns {boolean}
   */
  hasAppearanceDefaults: (indicatorKey) => {
    const defaults = get().customDefaults[indicatorKey];
    return !!(
      defaults?.appearanceConfig &&
      Object.keys(defaults.appearanceConfig).length > 0
    );
  },

  /**
   * Reset all custom defaults for all indicators
   */
  resetAllIndicatorsDefaults: async () => {
    set({ customDefaults: {} });
    await get().saveToServer();
  },
}));
